import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Slide,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { markNotificationAsRead } from "../../redux/slices/ecommerce/adminNotifictionSlice";
import notificationSound from "../../assets/sounds/universfield-new-notification-014-363678.mp3";

const MAX_VISIBLE_NOTIFICATIONS = 4;
const AUTO_DISMISS_MS = 8000;

const DesktopNotificationStack = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    latestRealtimeNotification,
    realtimeNotificationSequence,
    notificationTypes = [],
  } = useSelector((state) => state.adminNotification) || {};
  const [visibleNotifications, setVisibleNotifications] = useState([]);
  const dismissTimersRef = useRef({});

  const dismissNotification = useCallback((stackId) => {
    if (dismissTimersRef.current[stackId]) {
      window.clearTimeout(dismissTimersRef.current[stackId]);
      delete dismissTimersRef.current[stackId];
    }

    setVisibleNotifications((previous) =>
      previous.filter((item) => item.stackId !== stackId)
    );
  }, []);

  const typeMap = useMemo(() => {
    return notificationTypes.reduce((accumulator, item) => {
      accumulator[item?.value] = item?.name;
      return accumulator;
    }, {});
  }, [notificationTypes]);

  useEffect(() => {
    if (!latestRealtimeNotification?.notificationId || !realtimeNotificationSequence) {
      return;
    }

    const stackNotification = {
      ...latestRealtimeNotification,
      stackId: `${latestRealtimeNotification.notificationId}-${realtimeNotificationSequence}`,
    };

    playNotificationSound();

    setVisibleNotifications((previous) => {
      const withoutDuplicate = previous.filter(
        (item) =>
          Number(item.notificationId) !==
          Number(latestRealtimeNotification.notificationId)
      );

      return [stackNotification, ...withoutDuplicate].slice(
        0,
        MAX_VISIBLE_NOTIFICATIONS
      );
    });

    dismissTimersRef.current[stackNotification.stackId] = window.setTimeout(() => {
      dismissNotification(stackNotification.stackId);
    }, AUTO_DISMISS_MS);
  }, [dismissNotification, latestRealtimeNotification, realtimeNotificationSequence]);

  useEffect(() => {
    return () => {
      Object.values(dismissTimersRef.current).forEach((timerId) => {
        window.clearTimeout(timerId);
      });
    };
  }, []);

  const handleMarkAsRead = async (notification) => {
    if (!notification?.read) {
      await dispatch(markNotificationAsRead(notification.notificationId));
    }

    dismissNotification(notification.stackId);
  };

  const playNotificationSound = () => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      const audio = new Audio(notificationSound);
      audio.currentTime = 0;
      audio.play().catch((error) => {
        console.error("Failed to play notification sound", error);
      });
    } catch (error) {
      console.error("Failed to play notification sound", error);
    }
  };

  const resolveNotificationType = (notificationType) => {
    return typeMap[notificationType] || "Notification";
  };

  const formatNotificationTime = (notificationDate) => {
    if (!notificationDate) {
      return "Just now";
    }

    return moment.utc(notificationDate).local().fromNow();
  };

  if (!visibleNotifications.length) {
    return null;
  }

  return (
    <Box
      aria-live="polite"
      aria-atomic="false"
      sx={{
        position: "fixed",
        right: { xs: 12, sm: 20 },
        bottom: { xs: 12, sm: 20 },
        left: { xs: 12, sm: "auto" },
        zIndex: (muiTheme) => muiTheme.zIndex.snackbar + 10,
        pointerEvents: "none",
      }}
    >
      <Stack
        spacing={1.25}
        sx={{
          alignItems: { xs: "stretch", sm: "flex-end" },
        }}
      >
        {visibleNotifications.map((notification) => (
          <Slide
            key={notification.stackId}
            direction={isMobile ? "up" : "left"}
            in
            mountOnEnter
            unmountOnExit
          >
            <Paper
              elevation={0}
              sx={{
                width: { xs: "100%", sm: 380 },
                maxWidth: "100%",
                borderRadius: "8px",
                overflow: "hidden",
                border: "1px solid rgba(123, 80, 111, 0.16)",
                background: "rgba(255, 255, 255, 0.98)",
                boxShadow: "0 18px 45px rgba(52, 37, 46, 0.18)",
                pointerEvents: "auto",
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  display: "grid",
                  gridTemplateColumns: "40px minmax(0, 1fr) 32px",
                  gap: 1.25,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(123, 80, 111, 0.12)",
                    color: "#68485d",
                  }}
                >
                  <NotificationsRoundedIcon fontSize="small" />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      mb: 0.35,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 800,
                        color: "#68485d",
                        textTransform: "uppercase",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {resolveNotificationType(notification.notificationType)}
                    </Typography>
                    <Typography
                      sx={{
                        flexShrink: 0,
                        fontSize: "11px",
                        fontWeight: 700,
                        color: "rgba(52, 37, 46, 0.52)",
                      }}
                    >
                      {formatNotificationTime(notification.notificationDate)}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 800,
                      color: "#34252e",
                      lineHeight: 1.3,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {notification.notificationTitle || "New notification"}
                  </Typography>
                  {notification.notificationDescription && (
                    <Typography
                      sx={{
                        mt: 0.5,
                        fontSize: "13px",
                        lineHeight: 1.45,
                        color: "rgba(52, 37, 46, 0.72)",
                        display: "-webkit-box",
                        overflow: "hidden",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {notification.notificationDescription}
                    </Typography>
                  )}

                  <Button
                    size="small"
                    startIcon={<DoneRoundedIcon fontSize="small" />}
                    onClick={() => handleMarkAsRead(notification)}
                    sx={{
                      mt: 1,
                      minHeight: 28,
                      px: 1,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 800,
                      // color: "#68485d",
                      "&:hover": {
                        background: "rgba(123, 80, 111, 0.10)",
                      },
                    }}
                  >
                    Mark as read
                  </Button>
                </Box>

                <IconButton
                  aria-label="Dismiss notification"
                  size="small"
                  onClick={() => dismissNotification(notification.stackId)}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    color: "rgba(52, 37, 46, 0.62)",
                    "&:hover": {
                      background: "rgba(52, 37, 46, 0.08)",
                    },
                  }}
                >
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          </Slide>
        ))}
      </Stack>
    </Box>
  );
};

export default DesktopNotificationStack;
